/**
 * The project's MCP tab: like MCP Inspector, inside Flow, for the MCP servers the project's services expose (found in
 * their code or declared in `.flow/service.json` "mcp"). Pick a server on the left, connect (Streamable HTTP or SSE,
 * "Sign in as" a visitor, an app user, a token you give or your own Flow sign-in), then its tools, resources, resource templates and
 * prompts are listed with their schemas; a tool is called from a form made from its input schema (or raw JSON), and
 * its result shows its content blocks, structuredContent and isError. Calls go from vfs-service to the running service
 * through the preview (services/McpTesterService.js), with your own access to the project: a viewer lists and reads,
 * an editor also calls tools. A token stays in this browser session; History keeps this session's calls.
 *
 * A server that answers 401 / 403 gets "This server needs a sign-in" (McpSignInPanel: OAuth, your Flow sign-in, the
 * variable its code checks, an app user, a token), and "Sign in as" sits next to Connect from then on (or from the
 * start when the code shows the server checks a token). A stopped service is started by Connect, which then waits for
 * it; Auto says which transport it picked.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { PaperPlaneRight, PlugsConnected } from '@phosphor-icons/react';
import McpRail, { itemKey } from './McpRail';
import McpResult from './McpResult';
import McpSignInPanel from './McpSignInPanel';
import SchemaForm from './SchemaForm';
import { SignInPrompt } from '../api/ResponseCard';
import ServiceStartButton from '../sandbox/ServiceStartButton';
import { ErrorState, Loading, errorText } from '../data/DataCommon';
import { callMcp, connectMcp, getMcpServers } from '../../services/McpTesterService';
import { getAppUsers, signIn } from '../../services/ApiTesterService';
import { restartService } from '../../services/SandboxService';
import { startServices } from '../sandbox/startProgress';
import { userLabel } from '../../utils/apiTesterModel';
import {
  TRANSPORT_LABELS, addToHistory, clearHistory, exampleArgs, formToArgs, initialValues, parseRawArgs, prefersRaw, promptFields, readHistory,
  readToken, resultSummary, saveToken, schemaFields, serverKey, signInGuideOf, transportNote,
} from '../../utils/mcpTesterModel';
import '../data/Data.css';
import '../api/ApiTester.css';
import './McpTester.css';

const RESTART_WAIT_MS = 120_000;
const POLL_MS = 2000;
const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
const TOKEN_LABELS = { oauth: 'The OAuth sign-in token', secret: 'The token saved as a secret', manual: 'A bearer token you give' };

export const VIEWER_CALL = 'Viewers can list and read only: ask an editor of the project to call a tool';

const templateVars = (template) => [...String(template || '').matchAll(/\{([^}]+)\}/g)].map((m) => m[1].replace(/^[+#./;?&]/, ''));
const fillTemplate = (template, values) => String(template).replace(/\{([^}]+)\}/g, (_, raw) => encodeURIComponent(values[raw.replace(/^[+#./;?&]/, '')] ?? ''));

/** The form of an item: its fields, and whether raw JSON fits it better. */
function formOf(kind, item) {
  if (kind === 'tools') {
    const fields = schemaFields(item.inputSchema);
    return { fields, raw: prefersRaw(item.inputSchema, fields), rawText: exampleArgs(item.inputSchema) };
  }
  if (kind === 'prompts') { return { fields: promptFields(item), raw: false, rawText: '{}' }; }
  if (kind === 'resourceTemplates') {
    return { fields: templateVars(item.uriTemplate).map((name) => ({ name, kind: 'text', type: 'string', required: true, description: '', schema: {} })), raw: false, rawText: '{}' };
  }
  return { fields: [], raw: false, rawText: '{}' };
}

export default function McpPanel({ projectId, focus = null }) {
  const [state, setState] = useState({ loading: true, error: null, services: [], auth: null, canWrite: false });
  const [target, setTarget] = useState(null);
  const [transport, setTransport] = useState('auto');
  const [asValue, setAsValue] = useState('visitor');
  const [token, setToken] = useState(() => readToken(projectId));
  const [users, setUsers] = useState([]);
  const [connection, setConnection] = useState({ data: null, busy: false, error: null });
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ fields: [], values: {}, errors: {}, raw: false, rawText: '{}', rawError: null });
  const [answer, setAnswer] = useState(null);
  const [calling, setCalling] = useState(false);
  const [callError, setCallError] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [history, setHistory] = useState(() => readHistory(projectId));
  const [restarting, setRestarting] = useState(false);
  const [starting, setStarting] = useState(null);

  const userOf = (value) => users.find((u) => `user:${u.id}` === value) || null;
  const asPayload = (value = asValue, tok = token) => {
    if (value === 'token') { return { kind: 'token', header: { name: tok.name || 'Authorization', value: tok.value } }; }
    if (value === 'flow') { return { kind: 'flow' }; }
    const user = userOf(value);
    return user ? { kind: 'user', userId: user.id, service: state.auth?.service } : { kind: 'visitor' };
  };

  const load = useCallback(async() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const found = await getMcpServers(projectId);
      const services = found.services || [];
      setState({ loading: false, error: null, services, auth: found.auth || null, canWrite: found.canWrite !== false });
      setTarget((current) => {
        if (current) { return { ...current, running: services.find((s) => s.service === current.service)?.running || false }; }
        const wanted = focus?.service ? String(focus.service).replace(/^services\//, '') : null;
        const service = (wanted && services.find((s) => String(s.service).replace(/^services\//, '') === wanted && s.servers.some((x) => x.transport !== 'stdio')))
          || services.find((s) => s.running && s.servers.some((x) => x.transport !== 'stdio')) || services[0];
        const server = service?.servers.find((x) => x.transport !== 'stdio');
        return server ? { service: service.service, name: service.name, path: server.path, running: service.running, key: serverKey(service.service, server) } : null;
      });
    } catch (error) {
      setState((s) => ({ ...s, loading: false, error }));
    }
  }, [projectId, focus?.service]);
  useEffect(() => { load(); }, [load]);

  const authService = state.auth?.service || null;
  useEffect(() => {
    if (!authService) { setUsers([]); return undefined; }
    let live = true;
    getAppUsers(projectId, authService).then((found) => { if (live) { setUsers(found.users || []); } }).catch(() => { if (live) { setUsers([]); } });
    return () => { live = false; };
  }, [authService, projectId]);

  const connect = useCallback(async(to = target, as = null) => {
    if (!to) { return; }
    setConnection({ data: null, busy: true, error: null });
    setSelected(null);
    setAnswer(null);
    setCallError(null);
    const sent = as || asPayload();
    try {
      const data = await connectMcp(projectId, { service: to.service, path: to.path, transport, as: sent });
      setConnection({ data, busy: false, error: null });
      const first = data.tools?.[0];
      if (first) { pick('tools', first); }
    } catch (error) {
      const user = sent.kind === 'user' ? users.find((u) => u.id === sent.userId) || null : null;
      if (error.code === 'sign_in_needed' && user) { setPrompt({ user, busy: false, error: null, then: 'connect' }); }
      setConnection({ data: null, busy: false, error });
    }
    // asPayload and pick read the latest state on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, target, transport, asValue, token, users]);

  // A running server is connected to as soon as it is picked (and again when it starts).
  const targetKey = target ? `${target.key}|${target.running}` : null;
  useEffect(() => {
    if (target?.running) { connect(target); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  // A service on its way up is looked at again until it runs (then the effect above connects).
  const targetState = state.services.find((s) => s.service === target?.service)?.state || null;
  useEffect(() => {
    if (targetState !== 'building' && !starting) { return undefined; }
    const timer = setInterval(() => { load(); }, POLL_MS * 1.5);
    return () => clearInterval(timer);
  }, [targetState, starting, load]);

  /** Connect on a stopped service: start it, then the connection follows once it runs. */
  async function startAndConnect() {
    if (!target || starting) { return; }
    setStarting(target.service);
    setConnection({ data: null, busy: false, error: null });
    try {
      const last = await startServices(projectId, [{ serviceName: target.service, service: null }]);
      const entry = last?.services?.[0];
      if (entry && (entry.phase === 'failed' || entry.phase === 'limit')) {
        setConnection({ data: null, busy: false, error: new Error(entry.phase === 'limit' ? entry.reason : `${target.name} could not start: ${entry.reason || 'see Logs'}`) });
      }
    } catch (error) {
      setConnection({ data: null, busy: false, error });
    } finally {
      setStarting(null);
      await load();
    }
  }

  /** A sign-in picked in "This server needs a sign-in": kept as "Sign in as", then connected with it. */
  function applySignIn(choice, { connect: andConnect = true } = {}) {
    let as = null;
    if (choice.kind === 'token') {
      const next = { name: choice.name, value: choice.value, source: choice.source || 'manual' };
      changeToken(next);
      setAsValue('token');
      as = asPayload('token', next);
    } else if (choice.kind === 'flow') {
      setAsValue('flow');
      as = { kind: 'flow' };
    } else if (choice.kind === 'user') {
      const user = users.find((u) => String(u.id) === String(choice.id));
      if (!user) { return; }
      setAsValue(`user:${user.id}`);
      as = { kind: 'user', userId: user.id, service: state.auth?.service };
    }
    if (as && andConnect) { connect(target, as); }
  }

  /** After a secret was saved: the service restarted (it reads its secrets when it starts), then connected again. */
  async function restartAndConnect() {
    const sessionId = state.services.find((s) => s.service === target?.service)?.sessionId;
    if (!sessionId || restarting) { return; }
    setRestarting(true);
    try {
      await restartService(sessionId);
      const deadline = Date.now() + RESTART_WAIT_MS;
      for (;;) {
        await sleep(POLL_MS);
        const found = await getMcpServers(projectId).catch(() => null);
        if (found?.services?.find((s) => s.service === target.service)?.running || Date.now() > deadline) { break; }
      }
      await load();
      await connect(target);
    } catch (error) {
      setConnection({ data: null, busy: false, error });
    } finally {
      setRestarting(false);
    }
  }

  function pick(kind, item) {
    const made = formOf(kind, item);
    setSelected({ kind, item, key: itemKey(kind, item) });
    setForm({ fields: made.fields, values: initialValues(made.fields), errors: {}, raw: made.raw, rawText: made.rawText, rawError: null });
    setAnswer(null);
    setCallError(null);
  }

  const selectServer = (service, server) => {
    setTarget({ service: service.service, name: service.name, path: server.path, running: service.running, key: serverKey(service.service, server) });
    setTransport(server.transport === 'sse' ? 'sse' : 'auto');
    setConnection({ data: null, busy: false, error: null });
    setSelected(null);
  };

  const setRaw = (raw) => {
    if (raw) {
      const { args } = formToArgs(form.fields, form.values);
      setForm((f) => ({ ...f, raw: true, rawText: JSON.stringify(args, null, 2), rawError: null }));
      return;
    }
    const parsed = parseRawArgs(form.rawText);
    if (parsed.error) { setForm((f) => ({ ...f, rawError: parsed.error })); return; }
    const values = { ...form.values };
    form.fields.forEach((field) => {
      if (!(field.name in parsed.args)) { return; }
      const v = parsed.args[field.name];
      values[field.name] = field.kind === 'boolean' ? v === true : field.kind === 'json' ? JSON.stringify(v, null, 2) : String(v);
    });
    setForm((f) => ({ ...f, raw: false, values, rawError: null }));
  };

  /** The request of the selected item: `{ method, params, label }` or `{ error }`. */
  function requestOfSelected() {
    const { kind, item } = selected;
    let args;
    if (form.raw) {
      const parsed = parseRawArgs(form.rawText);
      if (parsed.error) { return { rawError: parsed.error }; }
      ({ args } = parsed);
    } else {
      const made = formToArgs(form.fields, form.values);
      if (Object.keys(made.errors).length) { return { errors: made.errors }; }
      ({ args } = made);
    }
    if (kind === 'tools') { return { method: 'tools/call', params: { name: item.name, arguments: args }, label: item.name }; }
    if (kind === 'prompts') { return { method: 'prompts/get', params: { name: item.name, arguments: Object.fromEntries(Object.entries(args).map(([k, v]) => [k, String(v)])) }, label: `prompt ${item.name}` }; }
    if (kind === 'resourceTemplates') { const uri = fillTemplate(item.uriTemplate, args); return { method: 'resources/read', params: { uri }, label: uri }; }
    return { method: 'resources/read', params: { uri: item.uri }, label: item.uri };
  }

  async function run(request = null) {
    if (!target || !selected || calling) { return; }
    const made = request || requestOfSelected();
    if (made.errors || made.rawError) { setForm((f) => ({ ...f, errors: made.errors || {}, rawError: made.rawError || null })); return; }
    if (made.method === 'tools/call' && !state.canWrite) { return; }
    setForm((f) => ({ ...f, errors: {}, rawError: null }));
    setCalling(true);
    setCallError(null);
    setPrompt(null);
    const as = asPayload();
    const entry = { service: target.service, path: target.path, method: made.method, params: made.params, label: made.label, kind: selected.kind, itemKey: selected.key };
    try {
      const got = await callMcp(projectId, { service: target.service, path: target.path, transport: connection.data?.transport || transport, method: made.method, params: made.params, as });
      setAnswer(got);
      setHistory(addToHistory(projectId, { ...entry, ok: !got.error && !got.result?.isError, isError: Boolean(got.result?.isError), summary: resultSummary(got), ms: got.ms }));
    } catch (error) {
      setAnswer(null);
      const user = userOf(asValue);
      if (error.code === 'sign_in_needed' && user) {
        setPrompt({ user, busy: false, error: null, then: 'call' });
      } else if (error.code === 'mcp_auth') {
        setConnection((c) => ({ ...c, error }));
      } else {
        setCallError(errorText(error));
      }
      if (error.code !== 'sign_in_needed') { setHistory(addToHistory(projectId, { ...entry, ok: false, summary: errorText(error) })); }
    } finally {
      setCalling(false);
    }
  }

  async function signInAndRetry(password) {
    setPrompt((p) => ({ ...p, busy: true, error: null }));
    try {
      await signIn(projectId, { service: authService, userId: prompt.user.id, password });
      const then = prompt.then;
      setPrompt(null);
      if (then === 'connect') { await connect(); } else { await run(); }
    } catch (error) {
      setPrompt((p) => ({ ...p, busy: false, error: errorText(error) }));
    }
  }

  const pickHistory = (entry) => {
    const items = connection.data?.[entry.kind] || [];
    const item = items.find((x) => itemKey(entry.kind, x) === entry.itemKey);
    if (item) {
      pick(entry.kind, item);
      if (entry.method === 'tools/call' || entry.method === 'prompts/get') {
        setForm((f) => ({ ...f, raw: true, rawText: JSON.stringify(entry.params?.arguments || {}, null, 2) }));
      }
    }
  };

  function changeToken(next) {
    setToken(next);
    saveToken(projectId, next);
  }

  const service = useMemo(() => state.services.find((s) => s.service === target?.service) || null, [state.services, target]);

  if (state.loading && !state.services.length) { return <div className="api-panel"><Loading label="Looking for MCP servers…" /></div>; }
  if (state.error) { return <div className="api-panel"><ErrorState error={errorText(state.error)} onRetry={load} /></div>; }
  if (!state.services.length) {
    return (
      <div className="api-panel mcp-empty-panel">
        <div className="api-empty mcp-empty">
          <strong>No MCP server found in this project yet.</strong>
          <p>A service that uses an MCP SDK (such as @modelcontextprotocol/sdk or FastMCP) over streamable HTTP or SSE shows here. You can also declare one in the service&apos;s <code>.flow/service.json</code>: <code>{'"mcp": { "path": "/mcp" }'}</code>.</p>
        </div>
      </div>
    );
  }

  const data = connection.data;
  const guide = signInGuideOf(connection.error, { hasUsers: users.length > 0 });
  const offersFlow = Boolean(guide?.options.some((o) => o.kind === 'flow'));
  const showAs = Boolean(guide) || Boolean(service?.signInLikely) || asValue !== 'visitor';
  const note = transportNote(transport, data);
  const canStartTarget = state.canWrite && service && service.state !== 'building';
  let connectLabel = data ? 'Reconnect' : 'Connect';
  if (connection.busy) { connectLabel = 'Connecting…'; } else if (restarting) { connectLabel = 'Restarting…'; } else if (starting || service?.state === 'building') { connectLabel = 'Starting…'; } else if (target && !target.running) { connectLabel = 'Start and connect'; }
  const isTool = selected?.kind === 'tools';
  const blocked = isTool && !state.canWrite;
  const actionLabel = { tools: 'Call tool', prompts: 'Get prompt', resources: 'Read', resourceTemplates: 'Read' }[selected?.kind] || 'Run';

  return (
    <div className="api-panel mcp-panel" data-testid="mcp-panel" onKeyDown={(event) => { if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) { event.preventDefault(); run(); } }}>
      <McpRail
        canStart={state.canWrite}
        connection={data}
        history={history}
        onClearHistory={() => setHistory(clearHistory(projectId))}
        onPickHistory={pickHistory}
        onSelectItem={pick}
        onSelectServer={selectServer}
        onStarted={load}
        projectId={projectId}
        selected={target?.key || null}
        selectedItem={selected?.key || null}
        services={state.services}
      />
      <main className="api-main">
        <div className="api-card api-bar" data-testid="mcp-bar">
          <span className="mcp-target data-mono" title={target ? `${target.service}${target.path}` : undefined}>{target ? `${target.name} ${target.path}` : 'Pick a server'}</span>
          <label className="data-sr" htmlFor="mcp-transport">Transport</label>
          <select className="data-input mcp-transport" id="mcp-transport" onChange={(event) => setTransport(event.target.value)} title="Auto tries Streamable HTTP, then SSE when the path does not take it" value={transport}>
            {['auto', 'streamable-http', 'sse'].map((t) => <option key={t} value={t}>{TRANSPORT_LABELS[t]}</option>)}
          </select>
          {note && <span className="data-muted mcp-transport-note" data-testid="mcp-transport-note">{note}</span>}
          {showAs && (
            <>
              <label className="data-label" htmlFor="mcp-as">Sign in as</label>
              <select className="data-input api-as" id="mcp-as" onChange={(event) => setAsValue(event.target.value)} value={userOf(asValue) || asValue === 'token' || asValue === 'flow' ? asValue : 'visitor'}>
                <option value="visitor">A visitor (no sign-in)</option>
                {users.map((user) => <option disabled={user.disabled} key={user.id} value={`user:${user.id}`}>{userLabel(user)}</option>)}
                <option value="token">{TOKEN_LABELS[token.source] || TOKEN_LABELS.manual}</option>
                {(asValue === 'flow' || offersFlow) && <option value="flow">My Flow sign-in</option>}
              </select>
            </>
          )}
          <button className="data-btn is-primary" data-testid="mcp-connect" disabled={!target || connection.busy || Boolean(starting) || restarting || (!target.running && !canStartTarget)} onClick={() => (target?.running ? connect() : startAndConnect())} type="button">
            <PlugsConnected aria-hidden size={15} />
            {connectLabel}
          </button>
          {asValue === 'token' && !guide && (
            <div className="api-token data-full" data-testid="mcp-token">
              <label className="data-sr" htmlFor="mcp-token-name">Header</label>
              <input className="data-input data-mono" id="mcp-token-name" onChange={(event) => changeToken({ ...token, name: event.target.value })} placeholder="Authorization" value={token.name} />
              <label className="data-sr" htmlFor="mcp-token-value">Value</label>
              <input autoComplete="off" className="data-input data-mono api-row-value" id="mcp-token-value" onChange={(event) => changeToken({ ...token, value: event.target.value })} placeholder="Bearer …" type="password" value={token.value} />
              <span className="data-muted">Kept for this browser session only and sent with each call; never stored in the project.</span>
            </div>
          )}
          {service && !service.running && (
            <p className="api-bar-note data-full api-group-stopped">
              <span>{service.name} is not running: start it to connect.</span>
              {state.canWrite && service.state !== 'building' && <ServiceStartButton onStarted={load} projectId={projectId} serviceName={service.service} />}
            </p>
          )}
          {prompt && (
            <div className="data-full">
              <SignInPrompt busy={prompt.busy} error={prompt.error} onCancel={() => setPrompt(null)} onSignIn={signInAndRetry} user={prompt.user} />
            </div>
          )}
          {guide && !prompt && target && (
            <McpSignInPanel
              canWrite={state.canWrite}
              guide={guide}
              key={`${target.key}|${guide.status}|${guide.options.map((o) => o.kind).join(',')}`}
              onRestart={service?.sessionId ? restartAndConnect : undefined}
              onUse={applySignIn}
              path={target.path}
              projectId={projectId}
              restarting={restarting}
              service={target.service}
              serviceName={target.name}
              token={token}
              users={users}
            />
          )}
          {connection.error && !prompt && !guide && (
            <p className="data-error-text data-full" data-testid="mcp-connect-error" role="alert">{errorText(connection.error)}</p>
          )}
          {data && (
            <div className="mcp-info data-full" data-testid="mcp-info">
              <strong>{data.server?.title || data.server?.name || 'MCP server'}</strong>
              {data.server?.version && <span className="data-muted">v{data.server.version}</span>}
              <span className="data-pill">{TRANSPORT_LABELS[data.transport] || data.transport}</span>
              {data.protocolVersion && <span className="data-muted">protocol {data.protocolVersion}</span>}
              <span className="data-muted">{Object.keys(data.capabilities || {}).join(', ') || 'no capabilities'}</span>
              <span className="data-muted">{data.ms} ms</span>
              {data.instructions && <details className="mcp-instructions"><summary>Instructions</summary><p>{data.instructions}</p></details>}
            </div>
          )}
        </div>
        {data && !selected && <p className="data-muted">Pick a tool, a resource or a prompt on the left.</p>}
        {data && !(data.tools?.length || data.resources?.length || data.resourceTemplates?.length || data.prompts?.length) && <p className="data-muted">This server offers no tools, resources or prompts.</p>}
        {selected && (
          <div className="api-split">
            <div className="api-card api-half mcp-item-card" data-testid="mcp-item">
              <div className="mcp-item-head">
                <strong className="data-mono">{selected.item.name || selected.item.uri || selected.item.uriTemplate}</strong>
                {selected.item.title && <span>{selected.item.title}</span>}
                {selected.item.annotations?.readOnlyHint && <span className="mcp-tag">read-only</span>}
                {selected.item.annotations?.destructiveHint && <span className="mcp-tag is-warn">destructive</span>}
              </div>
              {selected.item.description && <p className="mcp-desc">{selected.item.description}</p>}
              {(selected.item.uri || selected.item.uriTemplate) && <p className="data-mono data-muted mcp-desc">{selected.item.uri || selected.item.uriTemplate}{selected.item.mimeType ? ` · ${selected.item.mimeType}` : ''}</p>}
              <div className="api-part">
                {selected.kind !== 'resources' && (
                  <SchemaForm
                    errors={form.errors}
                    fields={form.fields}
                    onRaw={setRaw}
                    onRawText={(rawText) => setForm((f) => ({ ...f, rawText, rawError: null }))}
                    onValues={(values) => setForm((f) => ({ ...f, values, errors: {} }))}
                    raw={form.raw}
                    rawError={form.rawError}
                    rawText={form.rawText}
                    values={form.values}
                  />
                )}
                {isTool && (
                  <details className="mcp-schema">
                    <summary>Input schema</summary>
                    <pre className="data-mono mcp-text">{JSON.stringify(selected.item.inputSchema || {}, null, 2)}</pre>
                  </details>
                )}
                <div className="data-row-gap">
                  <button className="data-btn is-primary" data-testid="mcp-run" disabled={calling || blocked || !target?.running} onClick={() => run()} title={blocked ? VIEWER_CALL : `${actionLabel} (⌘↵)`} type="button">
                    <PaperPlaneRight aria-hidden size={15} />
                    {calling ? 'Calling…' : actionLabel}
                  </button>
                  {blocked && <span className="api-bar-note">{VIEWER_CALL}</span>}
                </div>
              </div>
            </div>
            <div className="api-half">
              <McpResult answer={answer} calling={calling} error={callError} outputSchema={isTool ? selected.item.outputSchema || null : null} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

McpPanel.propTypes = {
  focus: PropTypes.shape({ service: PropTypes.string }),
  projectId: PropTypes.string.isRequired,
};
